import { useState, useCallback, useMemo } from "react";
import { View, Text, Pressable, ScrollView } from "react-native";
import { SlidersHorizontal } from "lucide-react-native";
import { EMBER } from "../../../onboarding/theme";

import {
  CloseButton,
  Dialog,
  DialogActions,
  DialogHeader,
  PrimaryButton,
  MoonFilterChip,
  MoonCheckbox,
  SectionTitle,
  TextActionButton,
  cardSurface,
} from "@louise/moon-ui";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import type {
  GrantsFilters,
  GrantTreatment,
  ApplicationStatus,
} from "./grants.types";
import { useTranslations } from "@src/packages/i18n";
import { GRANTS } from "./grants.data";
import { filterGrants, grantTreatmentLabel } from "./grants.utils";
import { semanticColors, gradients } from "@louise/theme/tokens";
import { LinearGradient } from "expo-linear-gradient";

const TREATMENT_OPTIONS: GrantTreatment[] = [
  "IVF",
  "IUI",
  "Egg Freezing",
  "Donor Egg",
  "Embryo Adoption",
  "Surrogacy",
  "Adoption",
];

const STATUS_OPTIONS: {
  label?: string;
  labelKey?: string;
  value: ApplicationStatus;
}[] = [
  { labelKey: "costs.grant_filters.open_now", value: "open" },
  { label: "Upcoming", value: "upcoming" },
  { label: "Closed", value: "closed" },
];

function ToggleRow({
  label,
  active,
  onPress,
}: {
  label: string;
  active: boolean;
  onPress: () => void;
}) {
  return (
    <MoonCheckbox
      reverse
      label={label}
      value={active}
      onChange={onPress}
      accentColor={EMBER.colors.warm}
      style={{
        paddingVertical: 14,
        borderBottomWidth: 1,
        borderBottomColor: semanticColors.border.subtle,
      }}
    />
  );
}

type Props = {
  filters: GrantsFilters;
  onFiltersChange: (filters: GrantsFilters) => void;
};

export default function GrantsFilterModal({ filters, onFiltersChange }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("GrantsFilterModal");
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<GrantsFilters>(filters);

  const previewCount = useMemo(
    () => filterGrants(GRANTS, draft).length,
    [draft],
  );

  const handleOpen = useCallback(() => {
    // Icon-only trigger with no visible copy, so the descriptor is the control.
    capturePress(pressLabel("open_grants_filters"));
    setDraft({ ...filters });
    setOpen(true);
  }, [capturePress, filters]);

  const handleApply = useCallback(() => {
    onFiltersChange({ ...draft, search: filters.search });
    setOpen(false);
  }, [draft, filters.search, onFiltersChange]);

  const handleClear = useCallback(() => {
    setDraft({ search: filters.search });
  }, [filters.search]);

  const toggleTreatment = useCallback((treatment: GrantTreatment) => {
    setDraft((prev) => {
      const current = prev.treatments ?? [];
      const has = current.includes(treatment);
      return {
        ...prev,
        treatments: has
          ? current.filter((item) => item !== treatment)
          : [...current, treatment],
      };
    });
  }, []);

  const toggleStatus = useCallback((status: ApplicationStatus) => {
    setDraft((prev) => {
      const current = prev.applicationStatus ?? [];
      const has = current.includes(status);
      return {
        ...prev,
        applicationStatus: has
          ? current.filter((s) => s !== status)
          : [...current, status],
      };
    });
  }, []);

  const mainActiveCount = useMemo(() => {
    let count = 0;
    if (filters.treatments?.length) count += filters.treatments.length;
    if (filters.applicationStatus?.length) {
      count += filters.applicationStatus.length;
    }
    if (filters.matchesUser) count++;
    return count;
  }, [filters]);

  return (
    <>
      <Pressable
        onPress={handleOpen}
        style={{
          width: 44,
          height: 44,
          borderRadius: 22,
          ...cardSurface(),
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <SlidersHorizontal
          size={18}
          color={EMBER.colors.warm}
          strokeWidth={1.5}
        />
        {mainActiveCount > 0 && (
          <LinearGradient
            {...gradients.accent}
            style={{
              position: "absolute",
              top: -2,
              right: -2,
              width: 18,
              height: 18,
              borderRadius: 9,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 10,
                fontWeight: "700",
                color: semanticColors.text.inverse,
              }}
            >
              {mainActiveCount}
            </Text>
          </LinearGradient>
        )}
      </Pressable>

      <Dialog
        visible={open}
        layout="bottom"
        onClose={() => {
          // The header's close records its own press, so this is the
          // backdrop (or the system back), which has no visible copy.
          capturePress(pressLabel("dismiss_grants_filters"));
          setOpen(false);
        }}
      >
        <DialogHeader
          title="Filters"
          trailing={
            <CloseButton
              onPress={() => setOpen(false)}
              accessibilityLabel="Close"
            />
          }
          style={{ marginBottom: 4 }}
        />

        <ScrollView showsVerticalScrollIndicator={false}>
          <View style={{ marginTop: 20, marginBottom: 12 }}>
            <SectionTitle>
              {t("costs.grant_filters.treatment_type")}
            </SectionTitle>
          </View>
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
            {TREATMENT_OPTIONS.map((treatment) => (
              <MoonFilterChip
                key={treatment}
                label={grantTreatmentLabel(treatment, t)}
                selected={draft.treatments?.includes(treatment) ?? false}
                onPress={() => toggleTreatment(treatment)}
              />
            ))}
          </View>

          <View style={{ marginTop: 28, marginBottom: 12 }}>
            <SectionTitle>
              {t("costs.grant_filters.application_status")}
            </SectionTitle>
          </View>
          <View style={{ flexDirection: "row", gap: 8 }}>
            {STATUS_OPTIONS.map((opt) => (
              <MoonFilterChip
                key={opt.value}
                label={
                  opt.labelKey ? t(opt.labelKey) : (opt.label ?? opt.value)
                }
                selected={draft.applicationStatus?.includes(opt.value) ?? false}
                onPress={() => toggleStatus(opt.value)}
              />
            ))}
          </View>

          <View style={{ marginTop: 28, marginBottom: 4 }}>
            <SectionTitle>Other</SectionTitle>
          </View>
          <ToggleRow
            label={t("costs.grant_filters.matches_my_profile")}
            active={draft.matchesUser ?? false}
            onPress={() =>
              setDraft((prev) => ({
                ...prev,
                matchesUser: !prev.matchesUser,
              }))
            }
          />
        </ScrollView>

        {/* Outside the scroll area, so the actions stay in reach. */}
        <DialogActions style={{ alignItems: "center", marginTop: 16 }}>
          <TextActionButton label="Clear all" onPress={handleClear} />
          <PrimaryButton
            label={t(
              previewCount === 1
                ? "costs.grant_filters.show_grant_one"
                : "costs.grant_filters.show_grant_other",
              { params: { count: String(previewCount) } },
            )}
            onPress={handleApply}
            flex={1}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}
